/**
 * React-free helpers for the V2 drawer. Kept separate from the components so
 * they can be unit-tested in the node vitest config without pulling in the
 * component tree (map context, redux slice, SCSS imports).
 */
import dayjs from 'dayjs';

import type { AICatalog, AICommand } from '../hooks/useAICommands';
import {
  clearAiMapScopeFromSelections,
  getLayersAiMapScopeLabel,
} from '../utils/addSelectedLayerForAI';
import {
  getLayersFilterByDatetime,
  getSelectedFileIds,
  getSelectedLayers,
} from './dataSourceSelections';
import type { RealtimeSelection } from './types';

// ---------------------------------------------------------------------------
// Session validity
// ---------------------------------------------------------------------------

/** False when a selected source still needs configuration before sending. */
export const areSelectionsValid = (
  selections: RealtimeSelection[]
): boolean => {
  if (!selections.length) return true;
  const sitrep = selections.find((s) => s.source === 'SITREP');
  if (sitrep && !sitrep.id) return false;
  const layers = selections.find((s) => s.source === 'Layers');
  if (
    layers &&
    (!Array.isArray(layers.selectedLayers) ||
      layers.selectedLayers.length === 0)
  ) {
    return false;
  }
  return true;
};

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/** Strip legacy inline citation markers the model sometimes emits. */
export const cleanMessageContent = (content: string | undefined): string =>
  (content ?? '').replace(/\*\*\[[^\]]*\]\*\*|【[^】]*】/g, '');

export const formatMessageTimestamp = (
  timestamp: string | Date | undefined
): string =>
  timestamp
    ? dayjs(timestamp).format('YYYY-MM-DD HH:mm')
    : '(No datetime provided)';

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

export interface ChatSessionSummary {
  id: string;
  name: string;
  status?: string;
}

export const filterSessions = (
  sessions: ChatSessionSummary[],
  term: string
): ChatSessionSummary[] => {
  const q = term.trim().toLowerCase();
  if (!q) return sessions;
  return sessions.filter((s) => s.name.toLowerCase().includes(q));
};

// ---------------------------------------------------------------------------
// Scope chips
// ---------------------------------------------------------------------------

export interface ScopeChip {
  key: string;
  label: string;
  /** Omitted for informational chips (e.g. incident workspace). */
  onRemove?: () => void;
}

export interface BuildScopeChipsInput {
  selections: RealtimeSelection[];
  incidentName?: string;
  setSelections: (
    update: (prev: RealtimeSelection[]) => RealtimeSelection[]
  ) => void;
  /** Label for a datetime window applied to layers (from map context). */
  layersTimeLabel?: string;
}

export const buildScopeChips = ({
  selections,
  incidentName,
  setSelections,
  layersTimeLabel,
}: BuildScopeChipsInput): ScopeChip[] => {
  const chips: ScopeChip[] = [];
  if (incidentName) {
    chips.push({ key: 'incident', label: `Incident: ${incidentName}` });
  }
  const removeSource = (source: string) => () =>
    setSelections((prev) => prev.filter((s) => s.source !== source));

  for (const sel of selections) {
    switch (sel.source) {
      case 'Files': {
        const n = getSelectedFileIds(selections).length;
        chips.push({
          key: 'files',
          label: `Files (${n})`,
          onRemove: removeSource('Files'),
        });
        break;
      }
      case 'Layers': {
        const layers = getSelectedLayers(selections);
        chips.push({
          key: 'layers',
          label: `Layers (${layers.length})`,
          onRemove: removeSource('Layers'),
        });
        const timeLabel =
          getLayersFilterByDatetime(selections) &&
          (layers.map((l) => l.timeFilterLabel).find(Boolean) ||
            layersTimeLabel);
        if (timeLabel) {
          chips.push({
            key: 'layers-time',
            label: `At ${timeLabel}`,
            onRemove: () =>
              setSelections((prev) =>
                prev.map((s) =>
                  s.source === 'Layers'
                    ? {
                        ...s,
                        filterByDatetime: false,
                        selectedLayers: (s.selectedLayers ?? []).map((l) => {
                          const {
                            timeExtent: _t,
                            timeFilterLabel: _l,
                            ...rest
                          } = l;
                          return rest;
                        }),
                      }
                    : s
                )
              ),
          });
        }
        const area = getLayersAiMapScopeLabel(selections);
        if (area) {
          chips.push({
            key: 'layers-area',
            label: area,
            onRemove: () =>
              setSelections(
                (prev) =>
                  clearAiMapScopeFromSelections(prev) as RealtimeSelection[]
              ),
          });
        }
        break;
      }
      case 'ICS Form': {
        const custom = (sel.customData ?? {}) as {
          label?: string;
          formBaseId?: string;
          sectionSource?: string;
        };
        chips.push({
          key: `ics-${sel.id ?? custom.formBaseId ?? sel.source}`,
          label: custom.label || sel.description || 'ICS form',
          onRemove: () =>
            setSelections((prev) =>
              prev.filter(
                (item) =>
                  !(item.source === 'ICS Form' && item.id === sel.id)
              )
            ),
        });
        break;
      }
      default:
        chips.push({
          key: `src-${sel.source}`,
          label: sel.source,
          onRemove: removeSource(sel.source),
        });
    }
  }
  return chips;
};

// ---------------------------------------------------------------------------
// Empty state prompts
// ---------------------------------------------------------------------------

export interface SuggestedPrompt {
  label: string;
  prompt: string;
}

/** Fixed starters shown regardless of catalog contents. */
export const FIXED_STARTERS: SuggestedPrompt[] = [
  {
    label: 'Summarize the current incident',
    prompt:
      'Give me a concise situation summary of the current incident: objectives, open actions, and key risks.',
  },
  {
    label: 'ICS 201 gap analysis',
    prompt:
      'Run a gap analysis of our ICS 201 briefing and list the missing or weak sections.',
  },
  {
    label: 'Draft an automation rule',
    prompt:
      'Draft an automation rule that opens a High severity event when a hurricane forecast cone intersects any of our AORs.',
  },
];

/**
 * Merge fixed starters with the first skill example prompt from the AI catalog
 * (deduplicated by prompt text, capped so the empty state stays short).
 */
export const buildSuggestedPrompts = (
  catalog: AICatalog | undefined,
  max = 6
): SuggestedPrompt[] => {
  const seen = new Set<string>();
  const out: SuggestedPrompt[] = [];
  const push = (p: SuggestedPrompt) => {
    const key = p.prompt.trim().toLowerCase();
    if (!key || seen.has(key) || out.length >= max) return;
    seen.add(key);
    out.push(p);
  };
  FIXED_STARTERS.forEach(push);
  (catalog?.skills ?? []).forEach((skill) => {
    const example = skill.examplePrompts?.[0];
    if (example) push({ label: skill.name, prompt: example });
  });
  return out;
};

// ---------------------------------------------------------------------------
// Catalog grouping
// ---------------------------------------------------------------------------

export interface CommandGroup {
  kind: AICommand['kind'];
  group: string;
  items: AICommand[];
}

/** Skills (guided workflows) first, then tools; alphabetical within kind. */
export const groupCommands = (commands: AICommand[]): CommandGroup[] => {
  const byKey = new Map<string, CommandGroup>();
  for (const cmd of commands) {
    const key = `${cmd.kind}::${cmd.group}`;
    const entry = byKey.get(key) ?? {
      kind: cmd.kind,
      group: cmd.group,
      items: [],
    };
    entry.items.push(cmd);
    byKey.set(key, entry);
  }
  return [...byKey.values()].sort((a, b) =>
    a.kind === b.kind
      ? a.group.localeCompare(b.group)
      : a.kind === 'skill'
        ? -1
        : 1
  );
};
